import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
  TextField, Typography,
} from '@mui/material';
import { Add, Build, Delete, Edit, PlayArrow, TaskAlt } from '@mui/icons-material';
import api from '../../services/api';
import type {
  CompleteMaintenance, EquipmentOverview, FrequencyType, Machine, MaintenanceCalendarItem, MaintenancePlan, MaintenanceRecord,
  SaveMaintenancePlan, SaveMaintenanceRecord,
} from '../../types/factory';
import { formatDay } from '../../components/commercial/contact';
import { DUE_CHIP, EQUIPMENT_STATUS, Kpi, KpiStrip, PageHeader, StatusChip, addDaysIso, errorMessage, fmtMoney, fmtQty, isoDay, num, todayIso, usePageView } from './shared';
import type { PageView } from './shared';

const FREQ: Record<FrequencyType, string> = { Days: 'días', Hours: 'horas de uso', Cycles: 'ciclos' };
const TASK_KINDS = ['Revisión', 'Limpieza', 'Lubricación', 'Ajuste', 'Reemplazo de piezas'];
const RECORD_STATUS: Record<string, { label: string; color: 'info' | 'warning' | 'success' | 'default' }> = {
  Programado: { label: 'Programado', color: 'info' },
  EnCurso: { label: 'En curso', color: 'warning' },
  Realizado: { label: 'Realizado', color: 'success' },
  Cancelado: { label: 'Cancelado', color: 'default' },
};

type Supplier = { id: number; name: string };

const VIEWS: PageView[] = [
  { key: 'equipos', label: 'Equipos', description: 'Estado de cada máquina y matriz, con sus tareas vencidas y próximas.' },
  { key: 'tareas', label: 'Tareas de mantenimiento', description: 'Tareas preventivas de cada equipo: cada tantos días, horas de uso o ciclos.' },
  { key: 'calendario', label: 'Calendario de mantenimiento', description: 'Paradas previstas de las próximas semanas.' },
  { key: 'historial', label: 'Historial de mantenimiento', description: 'Intervenciones hechas, fallas y lo que costaron.' },
];

/** Mantenimiento preventivo de máquinas y matrices: ficha por equipo, tareas, calendario de paradas e historial. */
const FactoryMaintenancePage: React.FC = () => {
  const { tab, setTab, showTabs, flat, current } = usePageView(VIEWS);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [overview, setOverview] = useState<EquipmentOverview[] | null>(null);
  const [record, setRecord] = useState<Partial<SaveMaintenanceRecord> & { id?: number } | null>(null);
  const [completing, setCompleting] = useState<MaintenanceRecord | MaintenanceCalendarItem | null>(null);
  const [version, setVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const reload = () => setVersion(v => v + 1);
  useEffect(() => {
    api.getMachines(false).then(setMachines).catch(() => undefined);
    api.getSuppliers({ page: 1, pageSize: 500 }).then(r => setSuppliers((r.suppliers || []).map((s: any) => ({ id: s.id, name: s.name })))).catch(() => undefined);
  }, []);
  useEffect(() => {
    api.getMaintenanceOverview().then(setOverview).catch(e => setError(errorMessage(e, 'No se pudo cargar el mantenimiento')));
  }, [version]);

  const start = async (recordId: number) => {
    try {
      await api.startMaintenanceRecord(recordId);
      reload();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo iniciar'));
    }
  };

  const ctx: Ctx = { machines, suppliers, version, reload, openRecord: setRecord, complete: setCompleting, start };

  return (
    <Box>
      <PageHeader
        title={flat ? current.label : 'Mantenimiento'}
        description={flat && current.description ? current.description : <>Tareas preventivas de cada máquina y matriz (por fecha, horas o ciclos), paradas previstas y el historial de intervenciones y fallas.
            Lo que cuesta plata le llega a administración como gasto a pagar.</>}
        actions={<>
        <Button startIcon={<Build />} onClick={() => setRecord({ type: 'Correctivo', status: 'Realizado', doneAt: todayIso(), createExpense: true })}>
          Registrar falla / correctivo
        </Button>
        </>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {overview && (
        <KpiStrip>
          <Kpi label="Tareas vencidas" value={overview.reduce((a, o) => a + o.overdueCount, 0)} color="#d32f2f" />
          <Kpi label="Tareas próximas" value={overview.reduce((a, o) => a + o.soonCount, 0)} color="#ed6c02" />
          <Kpi label="Equipos en mantenimiento" value={overview.filter(o => o.status === 'EnMantenimiento').length} />
          <Kpi label="Fuera de servicio" value={overview.filter(o => o.status === 'FueraDeServicio').length} color={overview.some(o => o.status === 'FueraDeServicio') ? '#d32f2f' : undefined} />
        </KpiStrip>
      )}
      {showTabs && (<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Equipos" />
        <Tab label="Tareas" />
        <Tab label="Calendario" />
        <Tab label="Historial" />
      </Tabs>)}
      {tab === 0 && (!overview ? <CircularProgress /> : <EquipmentTab overview={overview} ctx={ctx} />)}
      {tab === 1 && <PlansTab ctx={ctx} />}
      {tab === 2 && <CalendarTab ctx={ctx} />}
      {tab === 3 && <HistoryTab ctx={ctx} />}

      {record && <RecordDialog initial={record} ctx={ctx} onClose={() => setRecord(null)} onSaved={() => { setRecord(null); reload(); }} />}
      {completing && <CompleteDialog item={completing} ctx={ctx} onClose={() => setCompleting(null)} onSaved={() => { setCompleting(null); reload(); }} />}
    </Box>
  );
};

type Ctx = {
  machines: Machine[];
  suppliers: Supplier[];
  version: number;
  reload: () => void;
  openRecord: (r: Partial<SaveMaintenanceRecord> & { id?: number }) => void;
  complete: (r: MaintenanceRecord | MaintenanceCalendarItem) => void;
  start: (recordId: number) => void;
};

// ==================== Equipos ====================

const EquipmentTab: React.FC<{ overview: EquipmentOverview[]; ctx: Ctx }> = ({ overview, ctx }) => (
  <Card variant="outlined">
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Equipo</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell align="right">Horas</TableCell>
            <TableCell align="right">Ciclos</TableCell>
            <TableCell>Mantenimiento</TableCell>
            <TableCell>Próxima intervención</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {overview.length === 0 && (
            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
              Cargá las máquinas y matrices en Producción → Máquinas y matrices.
            </TableCell></TableRow>
          )}
          {overview.map(o => (
            <TableRow key={o.machineId} hover>
              <TableCell>
                {o.code ? `${o.code} · ` : ''}{o.name}
                {o.kind === 'Matriz' && <Chip size="small" label="Matriz" sx={{ ml: 1 }} />}
              </TableCell>
              <TableCell><StatusChip map={EQUIPMENT_STATUS} value={o.status} /></TableCell>
              <TableCell align="right">{fmtQty(o.hours)}</TableCell>
              <TableCell align="right">{fmtQty(o.cycles)}</TableCell>
              <TableCell>
                {o.plansCount === 0 ? <Typography variant="caption" color="text.secondary">sin tareas</Typography> : (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <StatusChip map={DUE_CHIP} value={o.maintenanceStatus} />
                    <Typography variant="caption" color="text.secondary">{o.plansCount} tareas</Typography>
                  </Stack>
                )}
              </TableCell>
              <TableCell>
                {o.nextScheduledDate
                  ? <>Programada {formatDay(o.nextScheduledDate)}</>
                  : o.nextDueDate ? <>{o.nextDueTask} · {formatDay(o.nextDueDate)}</> : '—'}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Button size="small" onClick={() => ctx.openRecord({ machineId: o.machineId, type: 'Preventivo', status: 'Programado', plannedDate: addDaysIso(todayIso(), 7), createExpense: true })}>
                  Programar parada
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

// ==================== Tareas ====================

const emptyPlan = (machineId = 0): SaveMaintenancePlan => ({
  machineId, task: '', kind: 'Revisión', frequencyType: 'Days', frequencyValue: 30, lastDoneAt: todayIso(), lastDoneUsage: null,
  responsible: '', estimatedDowntimeHours: null, resources: '', estimatedCost: null, isActive: true, notes: '',
});

const PlansTab: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
  const [plans, setPlans] = useState<MaintenancePlan[] | null>(null);
  const [form, setForm] = useState<(SaveMaintenancePlan & { id?: number }) | null>(null);
  const [scheduling, setScheduling] = useState<MaintenancePlan | null>(null);
  const [date, setDate] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => { api.getMaintenancePlans().then(setPlans); }, []);
  useEffect(() => { load(); }, [load, ctx.version]);

  const save = async () => {
    if (!form) return;
    try {
      const { id, ...body } = form;
      if (id) await api.updateMaintenancePlan(id, body);
      else await api.createMaintenancePlan(body);
      setForm(null);
      setError(null);
      ctx.reload();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar la tarea'));
    }
  };

  const schedule = async () => {
    if (!scheduling) return;
    await api.scheduleMaintenancePlan(scheduling.id, { plannedDate: date });
    setScheduling(null);
    ctx.reload();
  };

  const remove = async (p: MaintenancePlan) => {
    if (!window.confirm(`¿Borrar la tarea "${p.task}" de ${p.machineName}?`)) return;
    await api.deleteMaintenancePlan(p.id);
    ctx.reload();
  };

  const remaining = (p: MaintenancePlan) =>
    p.frequencyType === 'Days'
      ? (p.remaining < 0 ? `vencida hace ${fmtQty(-p.remaining)} días` : `faltan ${fmtQty(p.remaining)} días`)
      : (p.remaining <= 0 ? `pasada por ${fmtQty(-p.remaining)} ${p.remainingUnit}` : `faltan ${fmtQty(p.remaining)} ${p.remainingUnit}`);

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setForm(emptyPlan(ctx.machines[0]?.id))} disabled={ctx.machines.length === 0}>Nueva tarea</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {!plans ? <CircularProgress /> : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Estado</TableCell>
                  <TableCell>Equipo</TableCell>
                  <TableCell>Tarea</TableCell>
                  <TableCell>Cada</TableCell>
                  <TableCell>Próxima</TableCell>
                  <TableCell>Responsable</TableCell>
                  <TableCell align="right">Parada</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin tareas de mantenimiento cargadas.</TableCell></TableRow>}
                {plans.map(p => (
                  <TableRow key={p.id} hover sx={{ opacity: p.isActive ? 1 : 0.5 }}>
                    <TableCell><StatusChip map={DUE_CHIP} value={p.dueStatus} /></TableCell>
                    <TableCell>{p.machineName}</TableCell>
                    <TableCell>{p.task}{p.kind && <Typography variant="caption" color="text.secondary" display="block">{p.kind}{p.resources ? ` · ${p.resources}` : ''}</Typography>}</TableCell>
                    <TableCell>{fmtQty(p.frequencyValue)} {FREQ[p.frequencyType]}</TableCell>
                    <TableCell>
                      {p.scheduledDate ? <Chip size="small" color="info" label={`Programada ${formatDay(p.scheduledDate)}`} /> : (
                        <>
                          {p.nextDueDate ? formatDay(p.nextDueDate) : '—'}
                          <Typography variant="caption" color="text.secondary" display="block">{remaining(p)}</Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell>{p.responsible || '—'}</TableCell>
                    <TableCell align="right">{p.estimatedDowntimeHours ? `${fmtQty(p.estimatedDowntimeHours)} h` : '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {!p.scheduledDate && <Button size="small" onClick={() => { setScheduling(p); setDate(isoDay(p.nextDueDate) > todayIso() ? isoDay(p.nextDueDate) : todayIso()); }}>Programar</Button>}
                      <IconButton size="small" onClick={() => setForm({ ...p, lastDoneAt: isoDay(p.lastDoneAt) || null })}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => remove(p)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.id ? 'Editar tarea' : 'Nueva tarea de mantenimiento'}</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField size="small" select label="Equipo" value={form.machineId || ''} onChange={e => setForm({ ...form, machineId: Number(e.target.value) })} sx={{ flex: 1 }}>
                  {ctx.machines.map(m => <MenuItem key={m.id} value={m.id}>{m.name}{m.kind === 'Matriz' ? ' (matriz)' : ''}</MenuItem>)}
                </TextField>
                <TextField size="small" select label="Tipo" value={form.kind || ''} onChange={e => setForm({ ...form, kind: e.target.value })} sx={{ width: 190 }}>
                  {TASK_KINDS.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </TextField>
              </Stack>
              <TextField size="small" label="Tarea" value={form.task} onChange={e => setForm({ ...form, task: e.target.value })} required placeholder="Lubricar guías y revisar resistencias" />
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="number" label="Cada" value={form.frequencyValue} onChange={e => setForm({ ...form, frequencyValue: num(e.target.value) })} sx={{ width: 110 }} />
                <TextField size="small" select label="Unidad" value={form.frequencyType} onChange={e => setForm({ ...form, frequencyType: e.target.value as FrequencyType })} sx={{ flex: 1 }}>
                  {(Object.keys(FREQ) as FrequencyType[]).map(k => <MenuItem key={k} value={k}>{FREQ[k]}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="date" label="Última vez" value={form.lastDoneAt || ''} onChange={e => setForm({ ...form, lastDoneAt: e.target.value || null })} InputLabelProps={{ shrink: true }} />
                {form.frequencyType !== 'Days' && (
                  <TextField size="small" type="number" label={`${FREQ[form.frequencyType]} en ese momento`} value={form.lastDoneUsage ?? ''} onChange={e => setForm({ ...form, lastDoneUsage: e.target.value === '' ? null : num(e.target.value) })} sx={{ flex: 1 }} />
                )}
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Responsable" value={form.responsible || ''} onChange={e => setForm({ ...form, responsible: e.target.value })} sx={{ flex: 1 }} />
                <TextField size="small" type="number" label="Parada estimada (h)" value={form.estimatedDowntimeHours ?? ''} onChange={e => setForm({ ...form, estimatedDowntimeHours: e.target.value === '' ? null : num(e.target.value) })} sx={{ width: 170 }} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Recursos" value={form.resources || ''} onChange={e => setForm({ ...form, resources: e.target.value })} sx={{ flex: 1 }} placeholder="Grasa, juego de resistencias, técnico externo" />
                <TextField size="small" type="number" label="Costo estimado $" value={form.estimatedCost ?? ''} onChange={e => setForm({ ...form, estimatedCost: e.target.value === '' ? null : num(e.target.value) })} sx={{ width: 160 }} />
              </Stack>
              <FormControlLabel control={<Checkbox checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />} label="Activa" />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!form?.task || !form?.machineId}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!scheduling} onClose={() => setScheduling(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Programar: {scheduling?.task}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{scheduling?.machineName} queda con una parada prevista ese día; se ve en la planificación de producción.</Typography>
            <TextField size="small" type="date" label="Fecha" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduling(null)}>Cancelar</Button>
          <Button variant="contained" onClick={schedule}>Programar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ==================== Calendario ====================

const CalendarTab: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
  const [items, setItems] = useState<MaintenanceCalendarItem[] | null>(null);
  useEffect(() => { api.getMaintenanceCalendar(todayIso(), addDaysIso(todayIso(), 60)).then(setItems); }, [ctx.version]);
  if (!items) return <CircularProgress />;
  const byDate = items.reduce<Record<string, MaintenanceCalendarItem[]>>((acc, i) => {
    (acc[isoDay(i.date)] ||= []).push(i);
    return acc;
  }, {});
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">Próximos 60 días. Las tareas por horas o ciclos se estiman con el uso del último mes.</Typography>
      {items.length === 0 && <Alert severity="success">No hay paradas ni tareas que venzan en los próximos 60 días.</Alert>}
      {Object.entries(byDate).map(([day, list]) => (
        <Card key={day} variant="outlined" sx={{ p: 1.5 }}>
          <Typography fontWeight={700} sx={{ mb: 0.5 }}>
            {new Date(`${day}T12:00:00Z`).toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })}
          </Typography>
          {list.map((i, k) => (
            <Stack key={k} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ py: 0.5 }}>
              <StatusChip map={DUE_CHIP} value={i.status} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                <b>{i.machineName}</b> · {i.title}
                {i.estimatedDowntimeHours ? ` · ${fmtQty(i.estimatedDowntimeHours)} h de parada` : ''}
                {i.responsible ? ` · ${i.responsible}` : ''}
                {i.source === 'plan' && <Typography component="span" variant="caption" color="text.secondary"> (estimado por la tarea)</Typography>}
              </Typography>
              {i.recordId && i.status === 'programado' && <Button size="small" startIcon={<PlayArrow />} onClick={() => ctx.start(i.recordId!)}>Iniciar</Button>}
              {i.recordId && <Button size="small" startIcon={<TaskAlt />} onClick={() => ctx.complete(i)}>Cerrar</Button>}
            </Stack>
          ))}
        </Card>
      ))}
    </Stack>
  );
};

// ==================== Historial ====================

const HistoryTab: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
  const [rows, setRows] = useState<MaintenanceRecord[] | null>(null);
  const [machineId, setMachineId] = useState<number | ''>('');
  useEffect(() => { api.getMaintenanceRecords({ machineId: machineId || undefined }).then(setRows); }, [machineId, ctx.version]);

  const remove = async (r: MaintenanceRecord) => {
    if (!window.confirm('¿Borrar esta intervención? Si generó un gasto, se anula desde Gastos.')) return;
    await api.deleteMaintenanceRecord(r.id);
    ctx.reload();
  };

  const downtime = (rows || []).filter(r => r.status === 'Realizado').reduce((a, r) => a + (r.actualDowntimeHours || 0), 0);

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField size="small" select label="Equipo" value={machineId} onChange={e => setMachineId(e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 220 }}>
          <MenuItem value="">Todos</MenuItem>
          {ctx.machines.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary">Horas de parada registradas: <b>{fmtQty(downtime)}</b></Typography>
      </Stack>
      {!rows ? <CircularProgress /> : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Equipo</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Trabajo / falla</TableCell>
                  <TableCell align="right">Parada</TableCell>
                  <TableCell align="right">Costo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin intervenciones registradas.</TableCell></TableRow>}
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{formatDay(r.doneAt || r.plannedDate)}</TableCell>
                    <TableCell>{r.machineName}</TableCell>
                    <TableCell>{r.type}</TableCell>
                    <TableCell>
                      {r.description || '—'}
                      {r.failure && <Typography variant="caption" color="error.main" display="block">Falla: {r.failure}</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      {r.actualDowntimeHours != null ? `${fmtQty(r.actualDowntimeHours)} h` : r.estimatedDowntimeHours ? `~${fmtQty(r.estimatedDowntimeHours)} h` : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {r.cost ? fmtMoney(r.cost) : '—'}
                      {r.expenseId && <Typography variant="caption" color="text.secondary" display="block">a pagar{r.supplierName ? ` · ${r.supplierName}` : ''}</Typography>}
                    </TableCell>
                    <TableCell><StatusChip map={RECORD_STATUS} value={r.status} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {r.status === 'Programado' && <IconButton size="small" title="Iniciar" onClick={() => ctx.start(r.id)}><PlayArrow fontSize="small" /></IconButton>}
                      {(r.status === 'Programado' || r.status === 'EnCurso') && <IconButton size="small" title="Cerrar" onClick={() => ctx.complete(r)}><TaskAlt fontSize="small" /></IconButton>}
                      <IconButton size="small" onClick={() => ctx.openRecord({ ...r, plannedDate: isoDay(r.plannedDate) || null, doneAt: isoDay(r.doneAt) || null, paymentDueDate: isoDay(r.paymentDueDate) || null, createExpense: !r.expenseId })}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => remove(r)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </>
  );
};

// ==================== Diálogos ====================

const SupplierPicker: React.FC<{ suppliers: Supplier[]; value?: number | null; onChange: (id: number | null) => void }> = ({ suppliers, value, onChange }) => (
  <Autocomplete
    size="small"
    options={suppliers}
    value={suppliers.find(s => s.id === value) || null}
    getOptionLabel={s => s.name}
    onChange={(_, v) => onChange(v?.id ?? null)}
    renderInput={p => <TextField {...p} label="Proveedor" />}
    sx={{ flex: 1 }}
  />
);

const RecordDialog: React.FC<{ initial: Partial<SaveMaintenanceRecord> & { id?: number }; ctx: Ctx; onClose: () => void; onSaved: () => void }> = ({ initial, ctx, onClose, onSaved }) => {
  const [f, setF] = useState<SaveMaintenanceRecord & { id?: number }>({
    machineId: ctx.machines[0]?.id ?? 0, type: 'Preventivo', status: 'Programado', createExpense: true, ...initial,
  } as SaveMaintenanceRecord & { id?: number });
  const [error, setError] = useState<string | null>(null);
  const done = f.status === 'Realizado';

  const save = async () => {
    try {
      const { id, ...body } = f;
      if (id) await api.updateMaintenanceRecord(id, body);
      else await api.createMaintenanceRecord(body);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{f.id ? 'Editar intervención' : f.type === 'Correctivo' ? 'Registrar falla / correctivo' : 'Programar parada'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField size="small" select label="Equipo" value={f.machineId || ''} onChange={e => setF({ ...f, machineId: Number(e.target.value), planId: null })} sx={{ flex: 1 }}>
              {ctx.machines.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Tipo" value={f.type} onChange={e => setF({ ...f, type: e.target.value as SaveMaintenanceRecord['type'] })} sx={{ width: 150 }}>
              <MenuItem value="Preventivo">Preventivo</MenuItem>
              <MenuItem value="Correctivo">Correctivo</MenuItem>
            </TextField>
            <TextField size="small" select label="Estado" value={f.status} onChange={e => setF({ ...f, status: e.target.value as SaveMaintenanceRecord['status'] })} sx={{ width: 150 }}>
              {Object.entries(RECORD_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField size="small" label="Trabajo" value={f.description || ''} onChange={e => setF({ ...f, description: e.target.value })} placeholder="Cambio de resistencias" />
          {done && <TextField size="small" label="Falla detectada" value={f.failure || ''} onChange={e => setF({ ...f, failure: e.target.value })} />}
          <Stack direction="row" spacing={2}>
            {done
              ? <TextField size="small" type="date" label="Fecha" value={f.doneAt || ''} onChange={e => setF({ ...f, doneAt: e.target.value || null })} InputLabelProps={{ shrink: true }} />
              : <TextField size="small" type="date" label="Fecha prevista" value={f.plannedDate || ''} onChange={e => setF({ ...f, plannedDate: e.target.value || null })} InputLabelProps={{ shrink: true }} />}
            <TextField size="small" type="number" label={done ? 'Parada real (h)' : 'Parada estimada (h)'} sx={{ width: 170 }}
              value={(done ? f.actualDowntimeHours : f.estimatedDowntimeHours) ?? ''}
              onChange={e => setF(done ? { ...f, actualDowntimeHours: e.target.value === '' ? null : num(e.target.value) } : { ...f, estimatedDowntimeHours: e.target.value === '' ? null : num(e.target.value) })} />
            <TextField size="small" label="Responsable" value={f.responsible || ''} onChange={e => setF({ ...f, responsible: e.target.value })} sx={{ flex: 1 }} />
          </Stack>
          <TextField size="small" label="Recursos (repuestos, servicio externo)" value={f.resources || ''} onChange={e => setF({ ...f, resources: e.target.value })} />
          <Stack direction="row" spacing={2}>
            <TextField size="small" type="number" label="Costo $" value={f.cost ?? ''} onChange={e => setF({ ...f, cost: e.target.value === '' ? null : num(e.target.value) })} sx={{ width: 140 }} />
            <SupplierPicker suppliers={ctx.suppliers} value={f.supplierId} onChange={id => setF({ ...f, supplierId: id })} />
            <TextField size="small" type="date" label="Vence el pago" value={f.paymentDueDate || ''} onChange={e => setF({ ...f, paymentDueDate: e.target.value || null })} InputLabelProps={{ shrink: true }} />
          </Stack>
          {done && (f.cost ?? 0) > 0 && (
            <FormControlLabel control={<Checkbox checked={f.createExpense} onChange={e => setF({ ...f, createExpense: e.target.checked })} />}
              label="Pasar a administración como gasto a pagar (Orden de pago)" />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={!f.machineId}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

const CompleteDialog: React.FC<{ item: MaintenanceRecord | MaintenanceCalendarItem; ctx: Ctx; onClose: () => void; onSaved: () => void }> = ({ item, ctx, onClose, onSaved }) => {
  const recordId = 'source' in item ? item.recordId! : item.id;
  const [f, setF] = useState<CompleteMaintenance>({
    doneAt: todayIso(), actualDowntimeHours: item.estimatedDowntimeHours ?? null, failure: '', cost: 'cost' in item ? item.cost ?? null : null,
    supplierId: 'supplierId' in item ? item.supplierId ?? null : null, paymentDueDate: null, notes: '', outOfService: false, createExpense: true,
  });
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      await api.completeMaintenanceRecord(recordId, f);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo cerrar'));
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cerrar intervención · {'machineName' in item ? item.machineName : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField size="small" type="date" label="Hecho el" value={f.doneAt || ''} onChange={e => setF({ ...f, doneAt: e.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="number" label="Parada real (h)" value={f.actualDowntimeHours ?? ''} onChange={e => setF({ ...f, actualDowntimeHours: e.target.value === '' ? null : num(e.target.value) })} />
          </Stack>
          <TextField size="small" label="Falla detectada" value={f.failure || ''} onChange={e => setF({ ...f, failure: e.target.value })} placeholder="Si no hubo, dejalo vacío" />
          <Stack direction="row" spacing={2}>
            <TextField size="small" type="number" label="Costo $" value={f.cost ?? ''} onChange={e => setF({ ...f, cost: e.target.value === '' ? null : num(e.target.value) })} sx={{ width: 140 }} />
            <SupplierPicker suppliers={ctx.suppliers} value={f.supplierId} onChange={id => setF({ ...f, supplierId: id })} />
            <TextField size="small" type="date" label="Vence el pago" value={f.paymentDueDate || ''} onChange={e => setF({ ...f, paymentDueDate: e.target.value || null })} InputLabelProps={{ shrink: true }} />
          </Stack>
          <TextField size="small" label="Notas" value={f.notes || ''} onChange={e => setF({ ...f, notes: e.target.value })} multiline />
          {(f.cost ?? 0) > 0 && <FormControlLabel control={<Checkbox checked={f.createExpense} onChange={e => setF({ ...f, createExpense: e.target.checked })} />} label="Pasar a administración como gasto a pagar (Orden de pago)" />}
          <FormControlLabel control={<Checkbox checked={f.outOfService} onChange={e => setF({ ...f, outOfService: e.target.checked })} />} label="El equipo queda fuera de servicio" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>Cerrar intervención</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FactoryMaintenancePage;
